import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { orgApi } from '../phase567Api';
import { errorMessage } from '../errors';
import PickSelect from '../shared/PickSelect';

// Ticket 202: add / remove one member at a time (POST and DELETE
// /v2/org/teams/{id}/members), so two admins editing the same team never
// overwrite each other's list. `team` is the live row from the page, so the
// list refreshes as soon as `onChanged` reloads it.
export default function TeamMembersDialog({ team, staff, onClose, onChanged }) {
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const ids = team?.member_user_ids || [];
  const label = (uid) => { const m = staff.find((x) => x.user_id === uid); return m ? (m.name || m.email) : 'Former member'; };
  const candidates = staff.filter((m) => !ids.includes(m.user_id));

  const run = async (fn, okTitle) => {
    setBusy(true);
    try {
      await fn();
      toast({ title: okTitle });
      await onChanged();
    } catch (e) {
      toast({ title: 'Could not update the team', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    if (!pick) return;
    await run(() => orgApi.addTeamMember(team.team_id, pick), 'Member added');
    setPick('');
  };

  return (
    <Dialog open={!!team} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ color: '#070B5F' }} className="font-serif">Members of {team?.name}</DialogTitle>
          <DialogDescription>Changes save as you make them. This is audited.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg max-h-64 overflow-y-auto" data-testid="team-member-list">
          {ids.length === 0 && <li className="px-3 py-4 text-sm text-slate-400 text-center">No members yet</li>}
          {ids.map((uid) => (
            <li key={uid} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`team-member-${uid}`}>
              <span>{label(uid)}</span>
              <button onClick={() => run(() => orgApi.removeTeamMember(team.team_id, uid), 'Member removed')} disabled={busy}
                className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Remove from team"
                aria-label={`Remove ${label(uid)}`} data-testid={`team-member-remove-${uid}`}>
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <PickSelect label="Add a person" value={pick} onChange={setPick} placeholder="Choose a team member" testId="team-member-pick"
              options={candidates.map((m) => ({ value: m.user_id, label: m.name || m.email }))} />
          </div>
          <button onClick={add} disabled={busy || !pick} className="btn-gold" data-testid="team-member-add">Add</button>
        </div>
        <div className="flex justify-end mt-2">
          <button onClick={onClose} className="btn-outline">Done</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
